import { useCallback, useEffect, useRef, useState } from 'react';

import clamp from 'lodash/clamp';

import {
	ACTION,
	ACTION_SUBJECT,
	ACTION_SUBJECT_ID,
	EVENT_TYPE,
} from '@atlaskit/editor-common/analytics';
import { logException } from '@atlaskit/editor-common/monitoring';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { findOverflowScrollParent } from '@atlaskit/editor-common/ui';
import { isEmptyParagraph } from '@atlaskit/editor-common/utils';
import {
	Slice,
	Fragment,
	type NodeType,
	type ResolvedPos,
	type Schema,
} from '@atlaskit/editor-prosemirror/model';
import {
	TextSelection,
	type ReadonlyTransaction,
	type Transaction,
} from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { TableMap } from '@atlaskit/editor-tables/table-map';
import { fg } from '@atlaskit/platform-feature-flags';
import { closeHistory, undo } from '@atlaskit/prosemirror-history';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { createUnifiedAnalyticsPayload } from '@atlassian/editor-ai-common/analytics/create-unified-analytics-payload';
import { ErrorUtils } from '@atlassian/editor-ai-common/utils/errors';

import type { AIPlugin } from '../../../aiPluginType';
import { replaceConfluenceTitle } from '../../../ui/prebuilt/config-items/actions/replace-title';
import type { EditorPluginAIConfigItem } from '../../../ui/prebuilt/config-items/config-items';
import { isSuggestTitleForConfluence } from '../../../ui/prebuilt/config-items/utils/suggest-title';
import { convertExperienceName } from '../../../ui/utils/analytics/analyticsFlow';
import { useAnalyticsFlow } from '../../../ui/utils/analytics/analyticsFlowUtils';
import type { Positions } from '../../../ui/utils/xstate/get-ai-experience-service';
import { startWatchingAnnotationChanges } from '../../ai-annotation-watchmen/transforms';
import { clearSelectionDecorations } from '../../decoration/actions';
import { aiExperienceDecorationPluginKey } from '../../decoration/decoration-plugin-key';
import { inEditorDirectStreamingPluginKey } from '../in-editor-direct-streaming-plugin-key';
import {
	adjustInitPositions,
	endStream,
	initPositions,
	metaTransaction,
	updateLoadingPositions,
	updateStreamPositions,
} from '../transforms';
import { isTableNode } from '../utils/node-check';
import { getPositionNodeTypeDepth, isWithinSameCell } from '../utils/position-checks';
import { calculateOffsetTopFromScrollContainer } from '../utils/scroll-calculations';

const SCROLL_MARGIN = 32;

/**
 * Initially, we
 * - map pos by using tr.mapping.map(pos, -1/+1)
 * - insert slice with both openStart and openEnd set to 1
 *
 * But when we insert a heading at the beginning of a paragraph,
 * the whole paragraph will be merged into the heading,
 * see https://www.loom.com/share/dd1855adcfeb49d0a8299ab5fea5a4cf
 * - extra problem: the first streaming chunk is kept in the doc, see the extra `The Fa` in the video
 *
 * The problem is that openStart 1 and openEnd 1 will cut 1 token from the start/end of the heading,
 * - see https://discuss.prosemirror.net/t/what-is-openstart-and-openend-use-for/3999
 * which I think make them merged together, so the solution seems like to set openStart and openEnd to 0
 * well, see the result https://www.loom.com/share/3cb1dff85242410ab56007f151fd6db5
 * the heading is inserted above the paragraph, but every streaming chunk is inserted as a new heading
 *
 * Now the problem is that the mapped startPos is always after the inserted heading
 * https://www.loom.com/share/b6903f080cee48348bfdbb71295ae787
 * see how the startPos changes every time we stream a chunk, it should be 1 all the time
 * in other words, the startPos shouldn't change at all while streaming
 * - see mapStartPos of how to fix this
 *
 * let's see what happens if we insert a heading at the end of a paragraph
 * https://www.loom.com/share/dad988651b2043f9b2b27ada172c2305
 * well, the endPos stays the same as the startPos all the time
 * https://www.loom.com/share/1697fe85612242bf9ffd4df19aef2014
 * but it should be changed to include the inserted heading
 * - see mapToPos of how to fix this
 *
 * let's see what happens if we insert a heading at the beginning of a list now
 * see https://www.loom.com/share/b7bf7ae2736242b490659bd0abf359ce
 * during streaming, there is an extra empty list item inserted
 * but when we restore the original user content, the extra list item is removed and the final response looks good
 * if you look at the beginning of the video, the fragment is just a heading, but the slice of the ReplaceStep contains 3 bullet list (WTF)
 *
 * how do we solve this problem, I think the best pos for the heading is right before the original list
 * we have to traverse the doc to find this correct pos
 * the same for inserting a heading at the end of a list
 */

export function mapStartPos({ pos, tr }: { pos: number; tr: ReadonlyTransaction | Transaction }) {
	let mappedPos = tr.mapping.map(pos, -1);

	// if it's AI in-document streaming, the startPos shouldn't change
	// however, when generating table, somehow the mappedPos could be smaller than pos
	// - we insert at the beginning of the doc, pos 1 somehow maps to 0
	// - which we should allow
	if (tr.getMeta('isAIStreamingTransformation')) {
		if (mappedPos > pos) {
			const adjustedPos = tr.mapping.map(pos - 1, -1) + 1;
			if (adjustedPos === pos) {
				mappedPos = pos;
			}
		}
	}

	return clamp(mappedPos, 0, tr.doc.content.size);
}

export function mapToPos({ pos, tr }: { pos: number; tr: ReadonlyTransaction | Transaction }) {
	let mappedPos = tr.mapping.map(pos, 1);

	// if it's AI in-document streaming, the endPos should change
	if (tr.getMeta('isAIStreamingTransformation')) {
		if (mappedPos === pos) {
			const adjustedPos = tr.mapping.map(pos + 1, 1) - 1;
			if (adjustedPos !== pos) {
				mappedPos = adjustedPos;
			}
		}
	}

	return clamp(mappedPos, 0, tr.doc.content.size);
}

function getStartOfTableRow({ schema, resolvedPos }: { resolvedPos: ResolvedPos; schema: Schema }) {
	for (let depth = resolvedPos.depth; depth > 0; depth--) {
		const node = resolvedPos.node(depth);
		if (node.type === schema.nodes.tableRow) {
			return resolvedPos.start(depth);
		}
	}
}

function getEndOfTableRow({ schema, resolvedPos }: { resolvedPos: ResolvedPos; schema: Schema }) {
	for (let depth = resolvedPos.depth; depth > 0; depth--) {
		const node = resolvedPos.node(depth);
		if (node.type === schema.nodes.tableRow) {
			const tableNode = resolvedPos.node(depth - 1);
			const tablePos = resolvedPos.start(depth - 1);
			const map = TableMap.get(tableNode);
			const { mapByRow } = map;
			const rowIndexContainingCell = mapByRow.findIndex((r) =>
				r.includes(resolvedPos.start(depth) - tablePos),
			);
			if (rowIndexContainingCell === -1) {
				/**
				 * In the case that we can't find a row containing the cell (this should not occur) we early exit
				 */
				return;
			}

			/**
			 * If we've reached the last row, then just return the end of that row
			 * since it won't have further vertically merged cells.
			 */
			if (rowIndexContainingCell + 1 === map.height) {
				const $cellPos = resolvedPos.doc.resolve(tablePos + mapByRow[rowIndexContainingCell][0]);
				return $cellPos.end($cellPos.depth);
			}

			for (let i = rowIndexContainingCell + 1; i < map.height; i++) {
				// We keep checking the next row to see if there are any duplicate positions
				// If there are, it means that there are vertically merged cells and we want to extend the position
				const rowA = mapByRow[i - 1];
				const rowB = mapByRow[i];

				if (rowA.every((val, idx) => val !== rowB[idx])) {
					const $cellPos = resolvedPos.doc.resolve(tablePos + rowA[0]);
					return $cellPos.end($cellPos.depth);
				}

				/***
				 * If we've reached the end and still encountering merged cells,
				 * we return the last row's end position
				 */
				if (i === map.height - 1) {
					const $cellPos = resolvedPos.doc.resolve(tablePos + rowB[0]);
					return $cellPos.end($cellPos.depth);
				}
			}
		}
	}
}

/**
 * We want to adjust the streaming position so that we don't break table rows.
 * Here, we extend the position to the end of a table row if the current position is inside a table cell.
 * We only do this if the replacement range spans over more than a cell!
 */
function adjustStartToPosForTable({
	view,
	startPos,
	toPos,
	userContentEndPos,
}: {
	fragment: Fragment;
	startPos: number;
	toPos: number;
	userContentEndPos: number;
	view: EditorView;
}) {
	const $startPos = view.state.doc.resolve(startPos);
	const $toPos = view.state.doc.resolve(toPos);

	const isSameCell = isWithinSameCell({
		$from: $startPos,
		$to: $toPos,
		schema: view.state.schema,
	});

	const adjustStartPosIfTableRow = !isSameCell
		? getStartOfTableRow({
				schema: view.state.schema,
				resolvedPos: $startPos,
			})
		: undefined;
	const adjustToPosIfTableRow = !isSameCell
		? getEndOfTableRow({
				schema: view.state.schema,
				resolvedPos: $toPos,
			})
		: undefined;

	if (adjustStartPosIfTableRow !== undefined) {
		startPos = adjustStartPosIfTableRow;
	}
	if (adjustToPosIfTableRow !== undefined) {
		toPos = Math.min(adjustToPosIfTableRow, userContentEndPos);
	}

	return { startPos, toPos };
}

/**
 * Adjusts toPos to account for bodiedSyncBlock nodes during intermediate streaming.
 *
 * During streaming, the pacer may defer emitting a bodiedSyncBlock for a tick.
 * This creates a mismatch: the doc still holds the existing bodiedSyncBlock, but
 * toPos (derived from aiContentEndPos) sits before it.
 *
 * - When the slice does NOT contain a bodiedSyncBlock, we cap toPos so that
 *   replaceRange does not accidentally remove the existing node.
 * - When the slice DOES contain a bodiedSyncBlock, we extend toPos past the
 *   existing node so that replaceRange replaces it rather than inserting a duplicate.
 */
function adjustStartToPosForSyncBlock({
	view,
	fragment,
	startPos,
	toPos,
	userContentEndPos,
}: {
	fragment: Fragment;
	startPos: number;
	toPos: number;
	userContentEndPos: number;
	view: EditorView;
}): { startPos: number; toPos: number } {
	const docSize = view.state.doc.content.size;

	// Step 1: Count sync blocks in the slice (the AI-generated fragment)
	let sliceSyncBlockCount = 0;
	fragment.forEach((node) => {
		if (node.type.name === 'bodiedSyncBlock') {
			sliceSyncBlockCount++;
		}
	});

	// Step 2: Map all sync blocks in the doc with their positions
	const docSyncBlocks: Array<{ end: number; localId: string; pos: number; resourceId: string }> =
		[];
	view.state.doc.content.forEach((node, offset) => {
		if (node.type.name === 'bodiedSyncBlock') {
			docSyncBlocks.push({
				pos: offset + 1, // +1 because forEach offset is relative to parent start
				end: offset + 1 + node.nodeSize,
				localId: node.attrs.localId,
				resourceId: node.attrs.resourceId,
			});
		}
	});

	if (sliceSyncBlockCount === 0) {
		// Step 3a: Slice has NO sync blocks.
		// We must cap toPos so replaceRange doesn't accidentally delete
		const $start = view.state.doc.resolve(startPos);

		// Case 1: startPos is inside a bodiedSyncBlock.
		// Cap toPos to the end of that block's inner content so
		// replaceRange stays within the block boundary.
		let capped = false;
		for (let d = $start.depth; d > 0; d--) {
			if ($start.node(d).type.name === 'bodiedSyncBlock') {
				const endOfContent = $start.end(d);
				if (toPos > endOfContent) {
					toPos = endOfContent;
				}
				capped = true;
				break;
			}
		}

		// Case 2: startPos is NOT inside a sync block.
		// Find the first sync block ahead of startPos and cap toPos before it.
		if (!capped) {
			view.state.doc.nodesBetween(startPos, Math.min(toPos, docSize), (node, pos) => {
				if (node.type.name === 'bodiedSyncBlock' && pos >= startPos && pos < toPos) {
					toPos = pos;
				}
				return false;
			});
		}
	} else {
		// Step 3b: Slice HAS sync block(s).
		// We need to make sure toPos covers the corresponding sync blocks
		// in the doc so replaceRange replaces them (not duplicates them).
		//
		// But we must NOT extend past sync blocks the slice doesn't cover,
		// or we'd remove them.
		//
		// Algorithm:
		//   - Count how many doc sync blocks are already within [startPos, toPos)
		//     (these are "covered" — replaceRange already reaches them)
		//   - needToExtendPast = sliceSyncBlockCount - coveredSyncBlocks
		//   - If > 0, scan forward from toPos and extend past that many sync blocks
		//   - If <= 0, no extension needed (all slice sync blocks already covered)

		// Count sync blocks fully covered by the replacement range [startPos, toPos).
		// A sync block is only "covered" if toPos extends past its END.
		// If toPos is in the MIDDLE of a sync block (start < toPos < end),
		// that block is NOT covered — we need to extend toPos past it.
		let coveredSyncBlocks = 0;
		const coveredDetails: Array<{
			end: number;
			fullyCovered: boolean;
			localId: string;
			pos: number;
		}> = [];
		view.state.doc.nodesBetween(startPos, Math.min(toPos, docSize), (node, pos) => {
			if (node.type.name === 'bodiedSyncBlock') {
				const end = pos + node.nodeSize;
				const fullyCovered = end <= toPos;
				if (fullyCovered) {
					coveredSyncBlocks++;
				}
				coveredDetails.push({
					pos,
					end,
					localId: node.attrs.localId,
					fullyCovered,
				});
			}
			return false;
		});

		const needToExtendPast = sliceSyncBlockCount - coveredSyncBlocks;

		if (needToExtendPast > 0) {
			let remaining = needToExtendPast;
			view.state.doc.nodesBetween(toPos, Math.min(userContentEndPos, docSize), (node, pos) => {
				if (remaining > 0 && node.type.name === 'bodiedSyncBlock') {
					const endOfNode = pos + node.nodeSize;
					if (endOfNode > toPos) {
						toPos = Math.min(endOfNode, userContentEndPos);
					}
					remaining--;
				}
				return false;
			});
		}
	}

	return { startPos, toPos };
}

/**
 * Ensure the fragment is valid, otherwise indicate we received the fragment, but make
 * no updates.
 *
 * @param fragment
 * @returns boolean - true if fragment is healthy (valid), false otherwise
 */
const fragmentIsHealthy = (fragment: Fragment) => {
	let err: unknown;
	let isHealthy = true;
	fragment.forEach((node) => {
		if (!isHealthy) {
			return;
		}
		try {
			node.check();
			// eslint-disable-next-line no-unused-vars
		} catch (e) {
			err = e;
			isHealthy = false;
		}
	});
	return { isHealthy, err };
};

// if we insert a heading at the beginning of a list item
// we want to insert the heading at the pos right before the list
// if we insert a heading at the end of a list item
// we want to insert the heading at the pos right after the list
function adjustStartEndPosForEmptySelection({
	view,
	fragment,
	startPos,
	userContentEndPos,
}: {
	fragment: Fragment;
	startPos: number;
	userContentEndPos: number;
	view: EditorView;
}) {
	const { paragraph, listItem, taskItem, decisionItem } = view.state.schema.nodes;
	const bailout = { startPos, userContentEndPos };

	const $start = view.state.doc.resolve(startPos);
	const startParent = $start.parent;

	// we only need to adjust the pos if the parent is list item (orderedList or bulletList), decisionItem, or taskItem
	const inList = startParent.type === paragraph && $start.node($start.depth - 1)?.type === listItem;
	const inTaskItem = startParent.type === taskItem;
	const inDecisionItem = startParent.type === decisionItem;
	const shouldAdjust = inList || inTaskItem || inDecisionItem;
	if (!shouldAdjust) {
		return bailout;
	}

	const $end = view.state.doc.resolve(userContentEndPos);
	const endParent = $end.parent;

	const newNode = fragment.firstChild;
	const oneBlock = fragment.childCount === 1 && newNode && newNode.isBlock;
	const newStartBlock = oneBlock && newNode.type.name !== startParent.type.name;
	const newEndBlock = oneBlock && newNode.type.name !== endParent.type.name;

	let startPosAdjusted = startPos;
	let endPosAdjusted = userContentEndPos;

	if (newStartBlock) {
		// find a potential pos at the beginning of the block
		for (let depth = $start.depth, diff = 1; depth > 0; depth--, diff++, startPosAdjusted--) {
			const ancestor = $start.node(depth);
			if (ancestor.type.contentMatch.matchType(newNode.type)) {
				break;
			}
			const before = $start.before(depth);
			if (before + diff !== startPos) {
				break;
			}
		}
	}

	if (newEndBlock) {
		// find a potential pos at the end of the block
		for (let depth = $end.depth, diff = 1; depth > 0; depth--, diff++, endPosAdjusted++) {
			const ancestor = $end.node(depth);
			if (ancestor.type.contentMatch.matchType(newNode.type)) {
				break;
			}
			const after = $end.after(depth);
			if (after - diff !== userContentEndPos) {
				break;
			}
		}
	}

	if (startPosAdjusted !== bailout.startPos || endPosAdjusted !== bailout.userContentEndPos) {
		// prefer the end if there is a tie/draw
		// or else if there will be extra list item if insert heading at the beginning of the doc
		const pos =
			endPosAdjusted - userContentEndPos >= startPos - startPosAdjusted
				? endPosAdjusted
				: startPosAdjusted;
		view.dispatch(adjustInitPositions({ start: pos, end: pos })(view.state.tr));
		return { startPos: pos, userContentEndPos: pos };
	}

	return bailout;
}

// if we are at the beginning of a block visually
// we want to move the start pos to the pos right before the block
// for example, if we run Improve Writing with two nodes: expand+paragraph
// we want to move the start pos to the pos right before the expand
// otherwise, there will be an extra empty expand node inserted
function adjustStartEndPosForRangeSelection({
	view,
	fragment,
	startPos,
	userContentEndPos,
}: {
	fragment: Fragment;
	startPos: number;
	userContentEndPos: number;
	view: EditorView;
}) {
	const { paragraph, tableCell, tableHeader } = view.state.schema.nodes;
	const bailout = { startPos, userContentEndPos };

	let $start = view.state.doc.resolve(startPos);

	// if the start pos is not at the beginning of its parent, bailout
	// sometimes startPos is 0, in this case $start.depth is 0 and
	// $start.before() will throw an error `There is no position before the top-level node`
	if ($start.depth > 0 && $start.before() !== startPos - 1) {
		return bailout;
	}

	// find the types of the ancestor nodes in the fragment in reverse order
	const types: NodeType[] = [];
	let node = fragment.firstChild;
	while (node && node.firstChild) {
		types.push(node.type);
		node = node.firstChild;
	}
	types.reverse();

	if (types.length === 1 && types[0] === paragraph) {
		return bailout;
	}

	if (fg('platform_editor_ai_replace_table_cell_issue_fix')) {
		/**
		 * If start pos is in a table cell or table header then bailout
		 * 	because adjustments for table cells are handled separately
		 * 	in adjustStartToPosForTable.
		 */
		const startPosNodeType = $start.parent.type;
		if (startPosNodeType === tableCell || startPosNodeType === tableHeader) {
			return bailout;
		}
	}
	// go to the innermost ancestor node in the doc
	let startPosAdjusted = startPos;
	node = $start.parent.firstChild;
	while (node && node.firstChild) {
		startPosAdjusted++;
		node = node.firstChild;
	}

	// compare every ancestor node outwards
	$start = view.state.doc.resolve(startPosAdjusted);
	for (let i = 0, depth = $start.depth; depth > 0; i++, depth--, startPosAdjusted--) {
		const ancestor = $start.node(depth);
		if (ancestor.type !== types[i]) {
			break;
		}
		const before = $start.before(depth);
		if (before + 1 !== startPosAdjusted) {
			break;
		}
	}

	// if we found a pos that is before startPos, we need to adjust startPos
	if (startPosAdjusted < bailout.startPos) {
		view.dispatch(
			adjustInitPositions({ start: startPosAdjusted, end: bailout.userContentEndPos })(
				view.state.tr,
			),
		);
		return { startPos: startPosAdjusted, userContentEndPos: bailout.userContentEndPos };
	}

	return bailout;
}

/**
 * Adjusts start and end positions when streaming block nodes (e.g., tables, panels) that replace content within list items.
 *
 * When AI streams a block-level node to replace selected content inside list items, we need to adjust
 * the replacement range to prevent empty list items from appearing before and after the inserted block.
 * This function expands the start and end positions to include the surrounding list item structure.
 *
 * @example
 * // Before adjustment: Replacing content inside a list item paragraph
 * // - List item 1
 * // - [Selected content in list item 2]
 * // - List item 2
 * // After streaming a table, without adjustment, would create:
 * // - List item 1
 * // -           <-- empty list item
 * // [Table]
 * // -           <-- empty list item
 * // - List item 2
 *
 * // With adjustment, positions are expanded to replace the entire list item:
 * // - List item 1
 * // [Table]
 * // - List item 2
 *
 * @param params - The adjustment parameters
 * @param params.fragment - The ProseMirror fragment being streamed (contains the AI-generated content)
 * @param params.startPos - The current start position of the replacement range
 * @param params.userContentEndPos - The current end position of the replacement range (original user content)
 * @param params.view - The editor view instance
 * @returns Object containing the adjusted start and end positions
 * @returns returns.startPos - The adjusted start position, moved earlier if at start of list item paragraph
 * @returns returns.userContentEndPos - The adjusted end position, moved later if at end of list item paragraph
 */
function adjustStartAndUserContentEndPosForList({
	fragment,
	startPos,
	userContentEndPos,
	view,
}: {
	fragment: Fragment;
	startPos: number;
	userContentEndPos: number;
	view: EditorView;
}) {
	const state = view.state;
	const schema = state.schema;
	const { listItem, paragraph } = schema.nodes;
	const doc = state.doc;
	let startPosAdjusted = startPos;
	let userContentEndPosAdjusted = userContentEndPos;

	/**
	 * If first child is empty then don't do any adjustments
	 */
	const fragmentFirstNode = fragment.firstChild;
	if (!fragmentFirstNode) {
		return { startPos: startPosAdjusted, userContentEndPos: userContentEndPosAdjusted };
	}

	/**
	 * If first child can be child of list item then no need to adjust positions.
	 * In case of table, layout or panel, first child won't be valid content of list item,
	 * 	thus adjustments will happen to avoid dangling list items.
	 */
	const firstChildFragment = Fragment.from(fragmentFirstNode);
	let shouldAdjustPositions = false;
	if (expValEquals('platform_editor_ai_aifc_listitem_indentation_fix', 'isEnabled', true)) {
		shouldAdjustPositions = !listItem?.validContent(firstChildFragment);
	} else {
		shouldAdjustPositions = fragmentFirstNode.isBlock;
	}
	if (shouldAdjustPositions) {
		const $start = doc.resolve(startPos);

		/**
		 * Adjusting startPos is only necessary when startPos is at the beginning of a paragraph in list item.
		 */
		let curParent = $start.node($start.depth);
		let curParentParent = $start.node($start.depth - 1);
		if (
			curParent.type === paragraph &&
			$start.parentOffset === 0 &&
			curParentParent?.type === listItem
		) {
			startPosAdjusted -= 2;
		} else if (curParent.type === listItem) {
			startPosAdjusted -= 1;
		}

		/**
		 * Adjusting userContentEndPos is only necessary when userContentEndPos is at the end of a paragraph in list item.
		 */
		const $userContentEnd = doc.resolve(userContentEndPos);
		curParent = $userContentEnd.node($userContentEnd.depth);
		curParentParent = $userContentEnd.node($userContentEnd.depth - 1);
		if (
			curParent.type === paragraph &&
			$userContentEnd.parentOffset === curParent.content.size &&
			curParentParent?.type === listItem
		) {
			userContentEndPosAdjusted += 2;
		} else if (curParent.type === listItem) {
			userContentEndPosAdjusted += 1;
		}
	}

	return { startPos: startPosAdjusted, userContentEndPos: userContentEndPosAdjusted };
}

function adjustSliceOpenStartEnd({
	slice,
	startPos,
	toPos,
	view,
}: {
	slice: Slice;
	startPos: number;
	toPos: number;
	view: EditorView;
}): Slice {
	const fragment = slice.content;

	// if the first node in fragment is different from the existing first node,
	// we need to adjust the slice open start to 0,
	// to avoid merging the first node in fragment with the existing first node,
	// for example, if the first node in fragment is a panel node, we'll lose the panel wrapper
	// (only the panel content is inserted into the doc)
	// but we don't need to do this if both nodes are table nodes
	const firstNode = fragment.firstChild;
	if (firstNode) {
		// bodiedSyncBlock must never be merged — its attrs (localId, resourceId)
		// must come from the streamed response, not the existing doc node.
		if (
			firstNode.type.name === 'bodiedSyncBlock' &&
			expValEquals('platform_editor_aifc_sync_block_stream_fix', 'isEnabled', true)
		) {
			slice = new Slice(fragment, 0, slice.openEnd);
		} else {
			const firstNodeIsTableNode = isTableNode({
				nodeType: firstNode.type,
				schema: view.state.schema,
			});
			const startPosTableDepth = getPositionNodeTypeDepth({
				resolvedPos: view.state.doc.resolve(startPos),
				nodeType: view.state.schema.nodes.table,
			});
			const bothTableNodes = firstNodeIsTableNode && !!startPosTableDepth;
			if (!bothTableNodes) {
				const existingFirstNode = view.state.doc.resolve(startPos).node(1);
				if (!existingFirstNode || firstNode.type !== existingFirstNode.type) {
					slice = new Slice(fragment, 0, slice.openEnd);
				}
			}
		}
	}

	// if the last node in fragment is different from the existing last node,
	// we need to adjust the slice open end to 0,
	// to avoid merging the last node in fragment with the existing last node,
	// for example, we've seen during streaming, heading and paragraph are merged together
	// (leading to a very long heading containing the paragraph content)
	// but we don't need to do this if both nodes are table nodes
	const lastNode = fragment.lastChild;
	if (lastNode) {
		// bodiedSyncBlock must never be merged — same reason as above.
		if (
			lastNode.type.name === 'bodiedSyncBlock' &&
			expValEquals('platform_editor_aifc_sync_block_stream_fix', 'isEnabled', true)
		) {
			slice = new Slice(fragment, slice.openStart, 0);
		} else {
			const lastNodeIsTableNode = isTableNode({
				nodeType: lastNode.type,
				schema: view.state.schema,
			});
			const toPosTableDepth = getPositionNodeTypeDepth({
				resolvedPos: view.state.doc.resolve(toPos),
				nodeType: view.state.schema.nodes.table,
			});
			const bothTableNodes = lastNodeIsTableNode && !!toPosTableDepth;
			if (!bothTableNodes) {
				const existingLastNode = view.state.doc.resolve(toPos).node(1);
				if (!existingLastNode || lastNode.type !== existingLastNode.type) {
					slice = new Slice(fragment, slice.openStart, 0);
				}
			}
		}
	}

	return slice;
}

export const useInEditorDirectStreaming = ({
	api,
	configItem,
	isProgrammaticScroll,
	view,
	positions,
	modalRef,
	aiSessionId,
}: {
	aiSessionId?: string;
	api: ExtractInjectionAPI<AIPlugin> | undefined;
	configItem: EditorPluginAIConfigItem;
	isProgrammaticScroll: React.MutableRefObject<boolean>;
	modalRef?: React.RefObject<HTMLDivElement>;
	positions: Positions;
	view: EditorView;
}) => {
	const isSuggestTitle = isSuggestTitleForConfluence(configItem);
	const showTelepointer = !isSuggestTitle;

	const [isPaused, setIsPaused] = useState(true);

	const [_start, setStart] = useState<number>();

	const scrollParentRef = useRef<HTMLElement | null>(null);
	const originalUserContentSlice = useRef<Slice>();
	/**
	 * The AI content slice is used to store the AI content that has been streamed
	 */
	const aiContentSlice = useRef<Slice>();

	const hasFinalStreamRef = useRef(false);

	// NOTE: This is not pretty but we still need data from the userflow, so we've decoupled the firing of unified events
	// for AIFC however we still need more data, so we can do this for now until xstate ios removed.
	const analyticsFlow = useAnalyticsFlow();
	const commonUnifiedAnalyticsData = analyticsFlow.getUnifiedEventData();

	useEffect(() => {
		const dom = view.dom;
		const scrollParent = findOverflowScrollParent(dom);
		if (scrollParent) {
			scrollParentRef.current = scrollParent;
		}
	}, [view]);

	useEffect(() => {
		if (_start !== undefined && scrollParentRef.current) {
			const startCoords = view.coordsAtPos(_start);
			const offsetTop = view.coordsAtPos(0).top;
			const modalHeight = modalRef?.current?.offsetHeight || 0;
			const offsetTopFromScrollContainer = calculateOffsetTopFromScrollContainer(
				view.dom,
				scrollParentRef.current,
			);

			/**
			 * If the start position is above viewport,
			 * we set the scroll so that the start is at the top of viewport (+ margin)
			 * If the start position is below the viewport,
			 * we set the scroll so that the start is at the bottom of viewport (+ margin)
			 */
			const topScrollTop =
				// offset of view dom from scroll parent
				offsetTopFromScrollContainer +
				// top - 0.top is height of content
				startCoords.top -
				offsetTop -
				// extra margin so text isn't at the very very top
				SCROLL_MARGIN;
			const bottomScrollTop =
				// offset of view dom from scroll parent
				offsetTopFromScrollContainer +
				// bottom - 0.top is height of content
				startCoords.bottom -
				offsetTop +
				// extra margin so modal isn't in the way
				modalHeight +
				// extra margin so text isn't at the very very bottom
				SCROLL_MARGIN -
				// subtracting offsetHeight keeps content at the bottom so
				// auto-scrolling is more incremental
				scrollParentRef.current.offsetHeight;

			isProgrammaticScroll.current = true;
			if (scrollParentRef.current.scrollTop > topScrollTop) {
				scrollParentRef.current.scroll({
					top: topScrollTop,
					behavior: 'instant',
				});
			} else if (scrollParentRef.current.scrollTop < bottomScrollTop) {
				scrollParentRef.current.scroll({
					top: bottomScrollTop,
					behavior: 'instant',
				});
			}
		}
	}, [view, _start, modalRef, isProgrammaticScroll]);

	const isEmptySelection = positions[0] === positions[1];
	const initialStartPos = positions[0];
	const shouldReplaceDoc = isEmptySelection && !configItem.insertOnEmptySelection;
	const [start, end] = shouldReplaceDoc ? [1, view.state.doc.content.size - 1] : positions;

	/**
	 * we should show highlight when:
	 * - always (yayyyyyyyyy)
	 *
	 * we should not show highlight when:
	 * - suggest title
	 */
	const showHighlight = !isSuggestTitle;
	// we don't want to show AI content highlight when we do full page streaming which replaces the whole document
	const showAIContentHighlight = !shouldReplaceDoc;

	useEffect(() => {
		if (
			// we don't need to show loading highlight when we're inserting into the current position such as free generate
			(isEmptySelection && configItem.insertOnEmptySelection) ||
			!showHighlight
		) {
			return;
		}
		const min = 80,
			max = 240;
		const prestreamLoading = setInterval(
			() => {
				if (isPaused) {
					return;
				}
				if (
					(inEditorDirectStreamingPluginKey.getState(view.state) || {}).aiContentEndPos !==
					undefined
				) {
					clearInterval(prestreamLoading);
				} else {
					view.dispatch(updateLoadingPositions(view.state.tr));
				}
			},
			Math.random() * (max - min) + min,
		);

		return () => {
			clearInterval(prestreamLoading);
		};
	}, [view, isPaused, configItem.insertOnEmptySelection, isEmptySelection, showHighlight]);

	const mountDecorations = useCallback(() => {
		const pluginState = inEditorDirectStreamingPluginKey.getState(view.state) || { chunkNumber: 0 };
		if (pluginState.startPos === undefined) {
			// turn diff off when starting streaming
			if (api?.trackChanges?.sharedState.currentState()?.isDisplayingChanges) {
				api?.core.actions.execute(api?.trackChanges?.commands.toggleChanges);
			}
			clearSelectionDecorations(view.state)(view.state, view.dispatch);
			setIsPaused(false);
			/**
			 * Store the original user content slice so that we can restore it
			 * and use it to manipulate the history plugin.
			 */
			originalUserContentSlice.current = view.state.doc.slice(start, end);
			const $start = view.state.doc.resolve(start);
			let telepointerPos = $start.pos;

			/**
			 * We position the telepointer to be the beginning of a leaf node so that
			 * it displays inline.
			 */
			let node = $start.node()?.firstChild;
			while (node && node.firstChild && !node.isLeaf) {
				telepointerPos += 1;
				node = node.firstChild;
			}
			const decorationPluginState = aiExperienceDecorationPluginKey.getState(view.state);

			view.dispatch(
				closeHistory(
					initPositions({
						start,
						end,
						showHighlight: showHighlight && decorationPluginState?.withStreamingHighlight !== false,
						showAIContentHighlight,
						showTelepointer,
						telepointerPos,
					})(startWatchingAnnotationChanges({ from: start, to: end })(view.state.tr)),
				),
			);
			setStart(start);
			return () => {
				setIsPaused(true);
			};
		}
	}, [api, view, start, end, showHighlight, showAIContentHighlight, showTelepointer]);

	/**
	 * Caller should dispatch an extra transaction to reset the document back to original state
	 * so that the history plugin can correctly track the difference between the
	 * original document and the final document after streaming.
	 */
	const restoreOriginalUserContent = useCallback(
		(tr: Transaction): Transaction => {
			const pluginState = inEditorDirectStreamingPluginKey.getState(view.state);
			const { startPos, userContentEndPos, streamTimeStamp } = pluginState || {};

			if (
				startPos === undefined ||
				userContentEndPos === undefined ||
				!originalUserContentSlice.current
			) {
				return tr;
			}

			// To review - size should never be -1 but it is sometimes
			if (originalUserContentSlice.current.size === -1) {
				// if the original user content is empty, we need to delete the inserted content
				tr = tr.delete(startPos, userContentEndPos);
				tr = metaTransaction({ tr, isFinalStream: false, stackTime: streamTimeStamp });
			} else {
				tr = tr.replaceRange(startPos, userContentEndPos, originalUserContentSlice.current);
				tr = metaTransaction({ tr, isFinalStream: false, stackTime: streamTimeStamp });
			}

			return tr;
		},
		[view],
	);

	const commitFinalResponse = useCallback(
		({ tr, slice }: { slice: Slice; tr: Transaction }): Transaction => {
			const pluginState = inEditorDirectStreamingPluginKey.getState(view.state);
			let { startPos, userContentEndPos } = pluginState || {};
			const streamTimeStamp = pluginState?.streamTimeStamp;

			if (
				startPos === undefined ||
				userContentEndPos === undefined ||
				!originalUserContentSlice.current
			) {
				return tr;
			}

			({ startPos, userContentEndPos } = adjustStartAndUserContentEndPosForList({
				view,
				fragment: slice.content,
				startPos,
				userContentEndPos,
			}));

			const { paragraph } = tr.doc.type.schema.nodes;
			// If we have a lone paragraph - replaceRange will not replace with a paragraph
			// This can cause issues as we will not be using the same localId as provided
			if (
				slice.content.firstChild === slice.content.lastChild &&
				slice.content.firstChild?.type === paragraph &&
				tr.selection.$from === tr.selection.$to &&
				isEmptyParagraph(tr.selection.$from.parent)
			) {
				tr = tr.replaceRangeWith(
					startPos,
					// End pos should never be smaller than start pos - for now just clamp it to start (to be reviewed)
					userContentEndPos < startPos ? startPos : userContentEndPos,
					slice.content.firstChild,
				);
			} else {
				tr = tr.replaceRange(
					startPos,
					// End pos should never be smaller than start pos - for now just clamp it to start (to be reviewed)
					userContentEndPos < startPos ? startPos : userContentEndPos,
					slice,
				);
			}

			tr = metaTransaction({ tr, isFinalStream: true, stackTime: streamTimeStamp });

			tr = tr.setSelection(
				TextSelection.create(tr.doc, Math.min(tr.selection.to, tr.doc.content.size)),
			);

			// the passed-in slice structure may be different from originalUserContentSlice
			// for a paragraph, the passed-in slice may contain only a text node
			// that's why we cut a new slice here
			const newSlice = tr.doc.slice(
				mapStartPos({ pos: startPos, tr }),
				mapToPos({ pos: userContentEndPos, tr }),
			);
			const isSame = newSlice.eq(originalUserContentSlice.current);
			if (isSame) {
				tr = tr.setMeta('addToHistory', false);
			}

			return tr;
		},
		[view],
	);

	const posAdjusted = useRef(false);

	/**
	 * We only add the final transaction to history so user can undo it all at once
	 */
	const updateStream = useCallback(
		({
			fragment,
			isFinalStream = false,
			// Prevent scroll-jacking by default to be safe
			hasUserScrolled = true,
		}: {
			fragment: Fragment;
			hasUserScrolled?: boolean;
			isFinalStream?: boolean;
		}): void => {
			try {
				if (isFinalStream) {
					if (hasFinalStreamRef.current) {
						return;
					}
					hasFinalStreamRef.current = true;
				}

				const scrollTop = scrollParentRef.current?.scrollTop;

				if (isSuggestTitle) {
					if (fg('platform_editor_ai_suggest_title_actioned_event')) {
						const actionedUnifiedEventPayload = createUnifiedAnalyticsPayload({
							action: ACTION.ACTIONED,
							...commonUnifiedAnalyticsData,
							interactionID: aiSessionId,
						});
						const discardedUnifiedEventPayload = createUnifiedAnalyticsPayload({
							action: ACTION.DISCARDED,
							...commonUnifiedAnalyticsData,
							interactionID: aiSessionId,
						});
						const fireUnifiedAnalyticsEvent = (eventType: 'actioned' | 'discarded') => {
							api?.analytics?.actions.fireAnalyticsEvent(
								eventType === 'actioned'
									? actionedUnifiedEventPayload
									: discardedUnifiedEventPayload,
							);
						};
						replaceConfluenceTitle({
							editorView: view,
							pmFragment: fragment,
							isFinal: isFinalStream,
							fireUnifiedAnalyticsEvent,
						});
					} else {
						replaceConfluenceTitle({ editorView: view, pmFragment: fragment });
					}

					if (
						isFinalStream &&
						originalUserContentSlice.current &&
						// We need to ensure we're not doubling up unified events. The streaming api (ie rovo chat) will also be firing
						// its own unified events. So we basically nuke all unified events from the streaming api.
						commonUnifiedAnalyticsData.invokedFrom !== 'streamingApi' &&
						commonUnifiedAnalyticsData.invokedFrom !== 'confluenceTitleGenOnPaste'
					) {
						// We fire the unified viewed event on the final stream because this is when all the AI content is known to be visible
						// to the user.
						// The view event previously fired in the preview modal. This will no longer occur in AIFC.
						api?.analytics?.actions.fireAnalyticsEvent(
							createUnifiedAnalyticsPayload({
								action: ACTION.VIEWED,
								...commonUnifiedAnalyticsData,
								interactionID: aiSessionId,
							}),
						);
					}

					return;
				}

				const pluginState = inEditorDirectStreamingPluginKey.getState(view.state);
				let { startPos, userContentEndPos } = pluginState || {};
				const { aiContentEndPos, streamTimeStamp } = pluginState || {};
				if (startPos === undefined || userContentEndPos === undefined) {
					return;
				}

				let slice = Slice.maxOpen(fragment);
				/**
				 * Occasionally we receive a response with empty content which causes the fragment -> slice
				 * to have a negative size, which causes the experience to break.
				 * Since there's nothing to update, we early exit here if it's not the final stream.
				 *
				 * We also want to early exit if the slice is inherently empty - for non empty selections we use maxOpen
				 * which means even if the fragment has an empty paragraph, the slice will have size 0 since the contents are empty.
				 */
				if (slice.size <= 0 && !isFinalStream) {
					return;
				}
				// https://atlassian-2y.sentry.io/issues/6886259844/?project=4505129224110080&query=tags%5Blocation%5D%3Aeditor-ai-streaming-update-stream&referrer=issue-stream
				// we're getting error `Cannot read properties of null (reading 'content')`
				// but we're using Slice.maxOpen(fragment)
				// maybe this will fix the issue
				if (!slice.content.firstChild) {
					slice = Slice.empty;
				}

				// If fragment isn't healthy indicate we received the chunk, but do not update positions as we haven't
				// streamed any changes
				const { isHealthy } = fragmentIsHealthy(fragment);
				if (!isHealthy) {
					// this is used to update the chunk number which is used to validate that the stream was "received" in orchestrator
					view.dispatch(
						updateStreamPositions({
							aiContentEndPos: pluginState?.aiContentEndPos,
							telepointerPos: pluginState?.telepointerPos,
							isFinalStream,
						})(view.state.tr),
					);

					if (
						expValEquals('platform_editor_ai_escape_early_for_unhealthy_node', 'isEnabled', true)
					) {
						// const errorMessage = `Unhealthy fragment detected - ${err instanceof Error ? err.message : String(err)}`;
						// For now disable this as it reports a lot of errors for no reason
						// api?.analytics?.actions.fireAnalyticsEvent({
						// 	action: ACTION.ERRORED,
						// 	actionSubject: ACTION_SUBJECT.AI_STREAMING,
						// 	actionSubjectId: ACTION_SUBJECT_ID.UPDATE_STREAM,
						// 	eventType: EVENT_TYPE.OPERATIONAL,
						// 	attributes: {
						// 		errorMessage,
						// 		errorStack: '',
						// 		isFinalStream,
						// 		docSize: view.state.doc.content.size,
						// 		fragmentSize: fragment.size,
						// 		experienceName: fg('platform_editor_ai_streaming_error_experience_name')
						// 			? convertExperienceName(configItem.key.toString())
						// 			: undefined,
						// 	},
						// });

						return;
					}
				}

				// we only need to adjust the pos once
				if (!posAdjusted.current) {
					posAdjusted.current = true;

					if (isEmptySelection) {
						({ startPos, userContentEndPos } = adjustStartEndPosForEmptySelection({
							view,
							fragment,
							startPos,
							userContentEndPos,
						}));
					} else {
						({ startPos } = adjustStartEndPosForRangeSelection({
							view,
							fragment,
							startPos,
							userContentEndPos,
						}));
						({ startPos, userContentEndPos } = adjustStartAndUserContentEndPosForList({
							view,
							fragment,
							startPos,
							userContentEndPos,
						}));
					}
				}

				let toPos = isFinalStream
					? userContentEndPos
					: Math.min(Math.max(aiContentEndPos || 0, startPos + slice.size + 1), userContentEndPos);

				({ startPos, toPos } = adjustStartToPosForTable({
					view,
					fragment,
					startPos,
					toPos,
					userContentEndPos,
				}));

				if (
					!isFinalStream &&
					expValEquals('platform_editor_aifc_sync_block_stream_fix', 'isEnabled', true)
				) {
					({ startPos, toPos } = adjustStartToPosForSyncBlock({
						view,
						fragment,
						startPos,
						toPos,
						userContentEndPos,
					}));
				}

				slice = adjustSliceOpenStartEnd({ slice, startPos, toPos, view });
				aiContentSlice.current = slice;
				// we don't want to add every streaming step to history
				let tr = view.state.tr;

				if (isFinalStream && originalUserContentSlice.current) {
					// we still need this restoration because it will allow
					// Prosemirror to drop fully covered parent nodes of the replaced region
					// if in the future we decide to remove this
					// we may need to revisit the logic to drop fully covered parent nodes
					// otherwise there will be empty parent nodes left in the doc
					view.dispatch(restoreOriginalUserContent(view.state.tr));
					// re-get pos after dispatch
					const _pluginState = inEditorDirectStreamingPluginKey.getState(view.state);
					startPos = _pluginState?.startPos || startPos;
					toPos = _pluginState?.userContentEndPos || toPos;

					// Now we replace it with the AI response
					tr = commitFinalResponse({ tr: view.state.tr, slice });

					if (
						// We need to ensure we're not doubling up unified events. The streaming api (ie rovo chat) will also be firing
						// its own unified events. So we basically nuke all unified events from the streaming api.
						commonUnifiedAnalyticsData.invokedFrom !== 'streamingApi'
					) {
						// We fire the unified viewed event on the final stream because this is when all the AI content is known to be visible
						// to the user.
						// The view event previously fired in the preview modal. This will no longer occur in AIFC.
						api?.analytics?.actions.fireAnalyticsEvent(
							createUnifiedAnalyticsPayload({
								action: ACTION.VIEWED,
								...commonUnifiedAnalyticsData,
								interactionID: aiSessionId,
							}),
						);
					}
				} else {
					tr = view.state.tr
						// End pos should never be smaller than start pos - for now just clamp it to start (to be reviewed)
						.replaceRange(startPos, toPos < startPos ? startPos : toPos, slice)
						// Local id updates break streaming as we may be trying
						// to replace a node that's already streamed in with
						// an empty local id -> causing the node to be destroyed and recreated
						// on the final stream we clean it up
						.setMeta('ignoreLocalIdUpdate', true);

					tr = metaTransaction({ tr, isFinalStream, stackTime: streamTimeStamp });
				}

				const mappedStartPos = mapStartPos({ pos: startPos, tr });
				let mappedToPos = mapToPos({ pos: toPos, tr });
				let telepointerPos = mappedToPos;
				tr.doc.nodesBetween(mappedStartPos, mappedToPos, (node, pos) => {
					if (node.isLeaf) {
						telepointerPos = pos + node.nodeSize;
					}
				});
				telepointerPos = Math.min(telepointerPos, mappedToPos);

				view.dispatch(
					updateStreamPositions({ aiContentEndPos: toPos, telepointerPos, isFinalStream })(tr),
				);

				// Expand all expand and nestedExpand nodes in the actual document after dispatching
				// Set expandedState after dispatch: only view.state.doc has the real nodes, not tr.doc.
				api?.core.actions.execute(
					api?.expand?.commands.toggleExpandRange(
						mappedStartPos,
						Math.min(mappedToPos, view.state.doc.content.size),
					),
				);

				isProgrammaticScroll.current = true;
				if (!hasUserScrolled && scrollParentRef.current) {
					const modalHeight = modalRef?.current?.offsetHeight || 0;

					// somehow we're getting pos out of range error here
					mappedToPos = clamp(mappedToPos, 0, view.state.doc.content.size);
					let toPosBottom = view.coordsAtPos(mappedToPos).bottom;
					// somehow this coords could be all 0 when the mappedToPos is the last position of the doc
					if (toPosBottom === 0) {
						toPosBottom = view.coordsAtPos(mappedToPos - 1).bottom;
					}

					const bottomScrollTop =
						// offset of view dom from scroll parent
						calculateOffsetTopFromScrollContainer(view.dom, scrollParentRef.current) +
						// mappedToPos.bottom - 0.top is height of content
						view.coordsAtPos(mappedToPos).bottom -
						view.coordsAtPos(0).top +
						// extra margin so modal isn't in the way
						modalHeight +
						// extra margin so text isn't at the very very bottom
						SCROLL_MARGIN -
						// subtracting offsetHeight keeps content at the bottom so
						// auto-scrolling is more incremental
						scrollParentRef.current.offsetHeight;

					// when isFinalStream is true, scrollParentRef.current.scrollTop is 0,
					// maybe it's because we reset the doc content before committing the final response,
					// but it doesn't affect the result if we adjust the scrollTop
					if (scrollParentRef.current.scrollTop < bottomScrollTop) {
						scrollParentRef.current?.scroll({
							top: bottomScrollTop,
							behavior: 'instant',
						});
					}
				} else {
					if (scrollParentRef.current && scrollParentRef.current.scrollTop !== scrollTop) {
						scrollParentRef.current.scroll({
							top: scrollTop,
							behavior: 'instant',
						});
					}
				}
			} catch (error) {
				const errorMessage = ErrorUtils.extractErrorMessage(error);
				let errorStack = '';
				if (error instanceof Error) {
					errorStack = error.stack || '';
					logException(error, {
						location: 'editor-ai-streaming-update-stream',
						isFinalStream,
						docSize: view.state.doc.content.size,
						fragmentSize: fragment.size,
					});
				}
				// https://atlassian-2y.sentry.io/issues/errors-outages/?project=4505129224110080&query=tags%5Blocation%5D%3Aeditor-ai-streaming-update-stream&referrer=issue-list&statsPeriod=14d
				api?.analytics?.actions.fireAnalyticsEvent({
					action: ACTION.ERRORED,
					actionSubject: ACTION_SUBJECT.AI_STREAMING,
					actionSubjectId: ACTION_SUBJECT_ID.UPDATE_STREAM,
					eventType: EVENT_TYPE.OPERATIONAL,
					attributes: {
						errorMessage,
						errorStack,
						isFinalStream,
						docSize: view.state.doc.content.size,
						fragmentSize: fragment.size,
						experienceName: fg('platform_editor_ai_streaming_error_experience_name')
							? convertExperienceName(configItem.key.toString())
							: undefined,
					},
				});
				if (isFinalStream) {
					throw error;
				}
			}
		},
		[
			api,
			view,
			modalRef,
			isSuggestTitle,
			restoreOriginalUserContent,
			commitFinalResponse,
			isEmptySelection,
			isProgrammaticScroll,
			commonUnifiedAnalyticsData,
			aiSessionId,
			configItem.key,
		],
	);

	const discardStream = useCallback(
		({ restoreSelection = true }: { restoreSelection?: boolean } = {}): void => {
			const pluginState = inEditorDirectStreamingPluginKey.getState(view.state);
			let { startPos, userContentEndPos } = pluginState || {};

			if (
				startPos === undefined ||
				userContentEndPos === undefined ||
				!originalUserContentSlice.current
			) {
				return;
			}

			try {
				// undo only if we already have streamed AI content
				// which means non need to undo if there is no network response yet or we haven't streamed anything yet
				if (!!aiContentSlice.current) {
					undo(view.state, view.dispatch);
					// re-get pos after dispatch
					const _pluginState = inEditorDirectStreamingPluginKey.getState(view.state);
					startPos = _pluginState?.startPos || startPos;
					userContentEndPos = _pluginState?.userContentEndPos || userContentEndPos;
				}

				let tr = view.state.tr;
				// We close the current history here.
				// We have a new history event when we do the discard, but when we close history we lock in the "mid-stream"
				// rather than an empty undo.
				closeHistory(tr);

				const decorationPluginState = aiExperienceDecorationPluginKey.getState(view.state);
				// If there was a session, don't restore the selection
				// because there might be multiple streaming updates, so we can't restore the selection
				if (restoreSelection && !decorationPluginState?.sessionEnded) {
					// restore user selection
					let anchor = 1;
					let head = 1;

					if (isEmptySelection) {
						anchor = initialStartPos;
						head = anchor;
					} else {
						anchor = mapStartPos({ pos: startPos, tr });
						head = mapToPos({ pos: userContentEndPos, tr });

						if (
							decorationPluginState &&
							decorationPluginState.initialSelection &&
							decorationPluginState.initialSelection.head <
								decorationPluginState.initialSelection.anchor
						) {
							[anchor, head] = [head, anchor];
						}
					}

					tr.setSelection(TextSelection.create(tr.doc, anchor, head));
				}

				// hide the highlight immediately after the stream is discarded
				// this is needed if we need to discard when the response is too similar
				tr = endStream(tr);

				view.dispatch(tr);
			} catch (error) {
				const errorMessage = ErrorUtils.extractErrorMessage(error);
				let errorStack = '';
				if (error instanceof Error) {
					errorStack = error.stack || '';
					logException(error, { location: 'editor-ai-streaming-discard-stream' });
				}
				api?.analytics?.actions.fireAnalyticsEvent({
					action: ACTION.ERRORED,
					actionSubject: ACTION_SUBJECT.AI_STREAMING,
					actionSubjectId: ACTION_SUBJECT_ID.DISCARD_STREAM,
					eventType: EVENT_TYPE.OPERATIONAL,
					attributes: {
						errorMessage,
						errorStack,
					},
				});
			} finally {
				// reset the stream state so that we can't discard again
				originalUserContentSlice.current = undefined;
				aiContentSlice.current = undefined;
			}
		},
		[api, view, isEmptySelection, initialStartPos],
	);

	return {
		mountDecorations,
		updateStream,
		discardStream,
		/**
		 * @private
		 * @deprecated	This method was only added for testing purposes within unified-analytics.tsx
		 * 				and should be removed as soon as we have a better way to emulate multiple
		 * 				"mini-streams" within a session (e.g. during free-gen)
		 */
		resetHasFinalStreamRef: (): boolean => (hasFinalStreamRef.current = false),
	};
};

export type StreamFns = Partial<ReturnType<typeof useInEditorDirectStreaming>>;
